import HUI from "@henge-inc/ui/v1_2";
import { UploadUnitPopover } from "@/engine/gaia/object/UploadUnitPopover";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";
import { DEFAULT_AZIMUTH_ANGLE, DEFAULT_POLAR_ANGLE } from "@/utils/cameraUtil";
import { mdiPlusCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { AnimatePresence } from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { UploadPanel } from "@/components/vault/slot/UploadPanel";

interface UploadVaultObjectTileProps {
  rowIndex: number;
  size?: number;
  pointerEvents?: "auto" | "none";
}

export const UploadSlot = ({
  rowIndex,
  size = 1,
  pointerEvents = "auto",
}: UploadVaultObjectTileProps) => {
  const { openVaultObjectUpload } = useVaultStore(
    (state) => state.vaultStoreActions,
  );

  const [create, setCreate] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (create) {
      const input = inputRef.current;
      if (input) {
        input.value = "";
      }
    }
  }, [create]);

  return (
    <div
      className={
        "pointer-events-none relative flex-shrink-0 flex justify-center items-center"
      }
      style={{
        zIndex: rowIndex + (size === 2 ? 0 : 1),
        width: "100%",
        height: "auto",
        aspectRatio: "2000 / 585",
        objectFit: "cover",
      }}
      onMouseLeave={() => {
        setCreate(false);
      }}
      onClick={() => {
        if (pointerEvents === "none") {
          return;
        }

        setCreate(true);
      }}
    >
      <input
        ref={inputRef}
        className={"hidden"}
        id={"open--dropzone"}
        type={"file"}
        onChange={(e) => {
          const { files } = e.target;
          if (files && files.length > 0) {
            openVaultObjectUpload(files);
          }
        }}
        multiple
      />

      <div
        className={"absolute -top-1/2 -bottom-1/2 left-0 right-0"}
        style={{
          width: "100%",
          height: "auto",
          aspectRatio: "1000 / 585",
        }}
      >
        <div
          className={"absolute bottom-[3%] left-1/2 -translate-x-1/2"}
          style={{
            width: `${size * 100 - 6}%`,
            height: "auto",
            aspectRatio: "1000 / 585",
            background: `center / cover url(${process.env.ASSET_URL ?? ""}/assets/images/gaia1000.png)`,
          }}
        >
          {/* Tile Boundary */}
          <div
            style={{
              pointerEvents: pointerEvents,
              cursor: "pointer",
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "67%",
              height: "auto",
              aspectRatio: "1 / 1",
              transform: `translate(-50%, -50.5%) rotateX(${
                (DEFAULT_POLAR_ANGLE * 180) / Math.PI
              }deg) rotateZ(${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
              backgroundColor: "transparent",
              borderWidth: "3px",
              borderStyle: "dashed",
              borderColor: HUI.HColour.Indigo100().getColour().oklch.getCSS(),
              borderRadius: "4%",
              zIndex: 0,
            }}
          />
          <span
            style={{
              pointerEvents,
              cursor: "pointer",
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "75%",
              height: "auto",
              aspectRatio: "1 / 1",
              transform: `translate(-50%, -50%) rotateX(${
                (DEFAULT_POLAR_ANGLE * 180) / Math.PI
              }deg) rotateZ(-${(DEFAULT_AZIMUTH_ANGLE * 180) / Math.PI}deg)`,
              borderTopRightRadius: "6%",
              borderBottomLeftRadius: "6%",
              borderTopLeftRadius: "9%",
              borderBottomRightRadius: "9%",
              border: "1px solid var(--border-tertiary)",
              overflow: "hidden",
              zIndex: 0,
            }}
          />
          {/* Tile Icon */}
          <Icon
            className={
              "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            }
            path={mdiPlusCircleOutline}
            size={"1.25rem"}
            color={HUI.HColour.Indigo100().getColour().oklch.getCSS()}
          />
          <AnimatePresence>
            {!create && <UploadUnitPopover/>}
            {create && <UploadPanel/>}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
